import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './PipelineBuilder.css';

const PipelineBuilder = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const [flow, setFlow] = useState(null);
  usePageTitle(flow ? `Edit ${flow.name}` : 'Pipeline Builder');
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (flowId) {
      fetchFlow();
    } else {
      // New flow
      setLoading(false);
    }
  }, [flowId]);

  const fetchFlow = async () => {
    try {
      const response = await axiosInstance.get(`/etl/flows/${flowId}/`);
      setFlow(response.data);
      // TODO: Fetch nodes for this flow
      setLoading(false);
    } catch (err) {
      console.error('Failed to load flow:', err);
      setLoading(false);
    }
  };

  const nodeTemplates = [
    {
      type: 'extract',
      icon: '📥',
      title: 'Extract from Odoo',
      description: 'Pull data from Odoo model',
      color: '#3b82f6',
      defaultConfig: {
        model: 'res.partner',
        fields: [],
        domain: [],
        batch_size: 1000
      }
    },
    {
      type: 'transform',
      icon: '⚙️',
      title: 'Transform Data',
      description: 'Clean, rename, or modify fields',
      color: '#8b5cf6',
      defaultConfig: {
        operations: []
      }
    },
    {
      type: 'validate',
      icon: '✅',
      title: 'Validate Data',
      description: 'Check data quality and rules',
      color: '#10b981',
      defaultConfig: {
        rules: []
      }
    },
    {
      type: 'load',
      icon: '📤',
      title: 'Load to BigQuery',
      description: 'Save data to destination',
      color: '#f59e0b',
      defaultConfig: {
        dataset: 'stg',
        table: '',
        write_disposition: 'WRITE_TRUNCATE'
      }
    }
  ];

  const addNode = (template) => {
    const newNode = {
      id: `node-${Date.now()}`,
      ...template,
      config: { ...template.defaultConfig },
      order: nodes.length,
      position: { x: 100, y: 100 + (nodes.length * 150) }
    };
    setNodes([...nodes, newNode]);
    setSelectedNode(newNode);
  };

  const updateNodeConfig = (nodeId, config) => {
    setNodes(nodes.map(node => 
      node.id === nodeId ? { ...node, config } : node
    ));
  };

  const removeNode = (nodeId) => {
    setNodes(nodes.filter(node => node.id !== nodeId));
    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
    }
  };

  const savePipeline = async () => {
    if (nodes.length === 0) {
      alert('Please add at least one node to your pipeline');
      return;
    }

    // For now, this visual builder is for planning only
    // Use the Flow Creator or Field Selector for actual flow creation
    alert('Visual Pipeline Builder is in preview mode.\n\nTo create actual flows:\n1. Use "Flows" → "Create Flow" for new ETL flows\n2. Use "Pipelines" to group existing flows together');
  };

  if (loading) {
    return <div className="pipeline-container"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="pipeline-container">
      <div className="pipeline-header">
        <div>
          <h1>Pipeline Builder</h1>
          <p className="page-description">
            {flowId ? `Edit pipeline for ${flow?.name}` : 'Create a new ETL pipeline'}
          </p>
        </div>
        <div className="header-actions">
          <button onClick={() => navigate('/flows')} className="btn-secondary">
            Cancel
          </button>
          <button onClick={savePipeline} className="btn-primary">
            💾 Save Pipeline
          </button>
        </div>
      </div>

      <div className="pipeline-workspace">
        {/* Node Palette */}
        <div className="node-palette">
          <h3>Add Nodes</h3>
          <div className="palette-grid">
            {nodeTemplates.map((template) => (
              <button
                key={template.type}
                className="palette-node"
                onClick={() => addNode(template)}
                style={{ borderColor: template.color }}
              >
                <span className="palette-icon">{template.icon}</span>
                <span className="palette-title">{template.title}</span>
                <span className="palette-description">{template.description}</span>
              </button>
            ))}
          </div>

          {/* Quick Templates */}
          <div className="templates-section">
            <h4>Quick Templates</h4>
            <button className="template-btn" onClick={() => loadTemplate('partners')}>
              👥 Partners Pipeline
            </button>
            <button className="template-btn" onClick={() => loadTemplate('products')}>
              📦 Products Pipeline
            </button>
            <button className="template-btn" onClick={() => loadTemplate('orders')}>
              🛒 Orders Pipeline
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="pipeline-canvas">
          {nodes.length === 0 ? (
            <div className="empty-canvas">
              <div className="empty-icon">🔧</div>
              <h3>Start Building Your Pipeline</h3>
              <p>Add nodes from the left panel to create your ETL workflow</p>
            </div>
          ) : (
            <div className="nodes-container">
              {nodes.map((node, index) => (
                <div key={node.id} className="pipeline-node-wrapper">
                  <div
                    className={`pipeline-node ${selectedNode?.id === node.id ? 'selected' : ''}`}
                    onClick={() => setSelectedNode(node)}
                    style={{ borderColor: node.color }}
                  >
                    <div className="node-header" style={{ background: node.color }}>
                      <span className="node-icon">{node.icon}</span>
                      <span className="node-title">{node.title}</span>
                      <button
                        className="node-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNode(node.id);
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="node-body">
                      <div className="node-config-preview">
                        {node.type === 'extract' && (
                          <div>
                            <strong>Model:</strong> {node.config.model || 'Not set'}
                          </div>
                        )}
                        {node.type === 'load' && (
                          <div>
                            <strong>Table:</strong> {node.config.dataset}.{node.config.table || 'Not set'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {index < nodes.length - 1 && (
                    <div className="node-connector">
                      <div className="connector-line"></div>
                      <div className="connector-arrow">↓</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Node Configuration Panel */}
        {selectedNode && (
          <div className="config-panel">
            <div className="config-header">
              <h3>{selectedNode.title}</h3>
              <button onClick={() => setSelectedNode(null)} className="close-btn">×</button>
            </div>
            
            <div className="config-body">
              {selectedNode.type === 'extract' && (
                <ExtractNodeConfig 
                  config={selectedNode.config}
                  onChange={(config) => updateNodeConfig(selectedNode.id, config)}
                />
              )}
              
              {selectedNode.type === 'transform' && (
                <TransformNodeConfig 
                  config={selectedNode.config}
                  onChange={(config) => updateNodeConfig(selectedNode.id, config)}
                />
              )}
              
              {selectedNode.type === 'validate' && (
                <ValidateNodeConfig 
                  config={selectedNode.config}
                  onChange={(config) => updateNodeConfig(selectedNode.id, config)}
                />
              )}
              
              {selectedNode.type === 'load' && (
                <LoadNodeConfig 
                  config={selectedNode.config}
                  onChange={(config) => updateNodeConfig(selectedNode.id, config)}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Node configuration components
const ExtractNodeConfig = ({ config, onChange }) => (
  <div className="node-config">
    <div className="form-group">
      <label>Odoo Model</label>
      <input
        type="text"
        value={config.model || ''}
        onChange={(e) => onChange({ ...config, model: e.target.value })}
        placeholder="e.g., res.partner"
        className="form-input"
      />
    </div>
    
    <div className="form-group">
      <label>Fields to Extract</label>
      <textarea
        value={config.fields?.join('\n') || ''}
        onChange={(e) => onChange({ ...config, fields: e.target.value.split('\n').filter(f => f.trim()) })}
        placeholder="One field per line&#10;id&#10;name&#10;email&#10;phone"
        className="form-textarea"
        rows="8"
      />
    </div>
    
    <div className="form-group">
      <label>Domain Filter (JSON)</label>
      <textarea
        value={JSON.stringify(config.domain || [], null, 2)}
        onChange={(e) => {
          try {
            onChange({ ...config, domain: JSON.parse(e.target.value) });
          } catch (err) {
            // Invalid JSON, don't update
          }
        }}
        placeholder='[["active", "=", true]]'
        className="form-textarea"
        rows="4"
      />
    </div>
    
    <div className="form-group">
      <label>Batch Size</label>
      <input
        type="number"
        value={config.batch_size || 1000}
        onChange={(e) => onChange({ ...config, batch_size: parseInt(e.target.value) })}
        className="form-input"
      />
    </div>
  </div>
);

const TransformNodeConfig = ({ config, onChange }) => (
  <div className="node-config">
    <div className="form-group">
      <label>Transformation Operations</label>
      <p className="help-text">Define how to transform your data</p>
      <textarea
        value={JSON.stringify(config.operations || [], null, 2)}
        onChange={(e) => {
          try {
            onChange({ ...config, operations: JSON.parse(e.target.value) });
          } catch (err) {
            // Invalid JSON
          }
        }}
        placeholder='[&#10;  {"type": "rename", "from": "name", "to": "partner_name"},&#10;  {"type": "cast", "field": "phone", "to": "string"}&#10;]'
        className="form-textarea"
        rows="10"
      />
    </div>
  </div>
);

const ValidateNodeConfig = ({ config, onChange }) => (
  <div className="node-config">
    <div className="form-group">
      <label>Validation Rules</label>
      <p className="help-text">Define data quality rules</p>
      <textarea
        value={JSON.stringify(config.rules || [], null, 2)}
        onChange={(e) => {
          try {
            onChange({ ...config, rules: JSON.parse(e.target.value) });
          } catch (err) {
            // Invalid JSON
          }
        }}
        placeholder='[&#10;  {"field": "id", "rule": "not_null"},&#10;  {"field": "email", "rule": "email_format"}&#10;]'
        className="form-textarea"
        rows="8"
      />
    </div>
  </div>
);

const LoadNodeConfig = ({ config, onChange }) => (
  <div className="node-config">
    <div className="form-group">
      <label>Dataset</label>
      <input
        type="text"
        value={config.dataset || 'stg'}
        onChange={(e) => onChange({ ...config, dataset: e.target.value })}
        placeholder="stg"
        className="form-input"
      />
    </div>
    
    <div className="form-group">
      <label>Table Name</label>
      <input
        type="text"
        value={config.table || ''}
        onChange={(e) => onChange({ ...config, table: e.target.value })}
        placeholder="partners"
        className="form-input"
      />
    </div>
    
    <div className="form-group">
      <label>Write Disposition</label>
      <select
        value={config.write_disposition || 'WRITE_TRUNCATE'}
        onChange={(e) => onChange({ ...config, write_disposition: e.target.value })}
        className="form-select"
      >
        <option value="WRITE_TRUNCATE">Truncate & Write</option>
        <option value="WRITE_APPEND">Append</option>
        <option value="WRITE_EMPTY">Write if Empty</option>
      </select>
    </div>
    
    <div className="form-group">
      <label className="checkbox-label">
        <input
          type="checkbox"
          checked={config.create_if_needed || false}
          onChange={(e) => onChange({ ...config, create_if_needed: e.target.checked })}
        />
        Create table if not exists
      </label>
    </div>
  </div>
);

// Template loader
const loadTemplate = (templateName) => {
  const templates = {
    partners: [
      {
        id: 'extract-partners',
        type: 'extract',
        icon: '📥',
        title: 'Extract Partners',
        color: '#3b82f6',
        order: 1,
        config: {
          model: 'res.partner',
          fields: ['id', 'name', 'email', 'phone', 'street', 'city', 'zip', 'country_id', 
                   'vat', 'is_company', 'create_date', 'write_date'],
          domain: [['active', '=', true]],
          batch_size: 1000
        }
      },
      {
        id: 'transform-partners',
        type: 'transform',
        icon: '⚙️',
        title: 'Clean & Transform',
        color: '#8b5cf6',
        order: 2,
        config: {
          operations: [
            { type: 'trim', fields: ['name', 'email', 'street', 'city'] },
            { type: 'lowercase', fields: ['email'] },
            { type: 'remove_nulls', fields: ['email'] }
          ]
        }
      },
      {
        id: 'validate-partners',
        type: 'validate',
        icon: '✅',
        title: 'Validate Partners',
        color: '#10b981',
        order: 3,
        config: {
          rules: [
            { field: 'id', rule: 'not_null' },
            { field: 'name', rule: 'not_empty' },
            { field: 'email', rule: 'email_format', optional: true }
          ]
        }
      },
      {
        id: 'load-partners',
        type: 'load',
        icon: '📤',
        title: 'Load to BigQuery',
        color: '#f59e0b',
        order: 4,
        config: {
          dataset: 'stg',
          table: 'partners',
          write_disposition: 'WRITE_TRUNCATE',
          create_if_needed: true
        }
      }
    ],
    products: [
      {
        id: 'extract-products',
        type: 'extract',
        icon: '📥',
        title: 'Extract Products',
        color: '#3b82f6',
        order: 1,
        config: {
          model: 'product.product',
          fields: ['id', 'name', 'default_code', 'list_price', 'standard_price', 
                   'type', 'categ_id', 'create_date', 'write_date'],
          domain: [['active', '=', true]],
          batch_size: 1000
        }
      },
      {
        id: 'load-products',
        type: 'load',
        icon: '📤',
        title: 'Load to BigQuery',
        color: '#f59e0b',
        order: 2,
        config: {
          dataset: 'stg',
          table: 'products',
          write_disposition: 'WRITE_TRUNCATE',
          create_if_needed: true
        }
      }
    ],
    orders: [
      {
        id: 'extract-orders',
        type: 'extract',
        icon: '📥',
        title: 'Extract Sale Orders',
        color: '#3b82f6',
        order: 1,
        config: {
          model: 'sale.order.line',
          fields: ['id', 'order_id', 'product_id', 'product_qty', 'price_unit', 
                   'price_total', 'state', 'create_date'],
          domain: [['order_id.state', 'not in', ['cancel', 'draft']]],
          batch_size: 1000
        }
      },
      {
        id: 'transform-orders',
        type: 'transform',
        icon: '⚙️',
        title: 'Transform Orders',
        color: '#8b5cf6',
        order: 2,
        config: {
          operations: [
            { type: 'add_timestamp', field: '_etl_loaded_at' },
            { type: 'flatten', nested_fields: ['order_id', 'product_id'] }
          ]
        }
      },
      {
        id: 'load-orders',
        type: 'load',
        icon: '📤',
        title: 'Load to BigQuery',
        color: '#f59e0b',
        order: 3,
        config: {
          dataset: 'stg',
          table: 'sale_order_lines',
          write_disposition: 'WRITE_APPEND',
          create_if_needed: true
        }
      }
    ]
  };

  if (templates[templateName]) {
    setNodes(templates[templateName]);
    setSelectedNode(templates[templateName][0]);
  }
};

export default PipelineBuilder;

